<template>
  <m-card-item
    v-if="status !== USER_STATUS_ARCHIVED"
    :disabled="props.user.uid === loggedInUser.uid"
    icon="archive"
    :tooltip="$t('userListCard.deactivateTooltip')"
    tooltip-placement="left"
    hide-border
    @click="confirmArchive"
  >
    {{ $t('general.deactivate') }}
  </m-card-item>
  <m-card-item
    v-if="status === USER_STATUS_ARCHIVED"
    icon="archive"
    hide-border
    :disabled="!mayRestore"
    :tooltip="mayRestore ? '' : $t('userListCard.restoreDisabled')"
    @click="restore"
  >
    {{ $t('general.reactivate') }}
  </m-card-item>
</template>

<script setup>
import useConfirmDialog from '@/composables/confirm-dialog';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useRepo from '@/nebula/repo';
import useSnackBar from '@/composables/snackbar';
import useUsers from '@/composables/user/users';
import { USER_STATUS_ARCHIVED } from '@/lib/constants';
import { computed } from 'vue';
import { failureCode } from 'shared/constants.json';
import { failureCodeOf, logCatch } from '@/lib/logger/logger';
import { provisionedUser as provisionedUserConfig } from 'shared/api/query/configs.json';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const { loggedInUser } = useLoggedInUser();
const snackbar = useSnackBar();
const confirmDialog = useConfirmDialog();
const provisionedUserRepo = useRepo(provisionedUserConfig.model);
const { t } = useI18n();

const { updateUser, getActivationStatus } = useUsers();
const status = computed(() => getActivationStatus(props.user));

const provisionedUser = computed(() => provisionedUserRepo.entityList.value.find((pu) => pu.externalId === props.user.externalId));

const mayRestore = computed(() => {
  if (provisionedUser.value === undefined) {
    return true;
  }

  return provisionedUser.value.deactivatedAt !== null;
});

const confirmArchive = () => {
  const archiveMethod = () => updateUser({ uid: props.user.uid, archivedAt: new Date() }).then(() => {
    snackbar.success(t('success.deactivated'));
  }).catch(logCatch((e) => {
    snackbar.error(failureCodeOf(e) === failureCode.lastAdmin ? t('error.lastAdmin') : t('error.default'));
  }, [failureCode.lastAdmin]));

  confirmDialog.confirm({
    title: t('userList.archiveUserTitle'),
    okText: t('general.yesDeactivate'),
    okType: 'danger',
    cancelText: t('general.cancel'),
    maskClosable: true,
    onOk() {
      archiveMethod();
    },
  });
};

const restore = () => {
  updateUser({ uid: props.user.uid, archivedAt: null }).then(() => {
    snackbar.success(t('success.restored'));
  }).catch(() => {
    snackbar.error(t('error.default'));
  });
};
</script>
